import { Tooltip } from "@openanalyst-ai/ui/tooltip"
import { A, useLocation } from "@solidjs/router"
import { createMemo, For, Show, type Accessor, type JSX } from "solid-js"
import { getAvatarColors } from "@/context/layout"
import { PluginAppIcon } from "@/plugins/plugin-app-icon"
import { canonicalPluginAppID, type RuntimePluginApp } from "@/plugins/runtime"
import { emitOpenPluginPanelTab } from "@/pages/session/plugin-panel-tabs"
import { pluginColorForIndex, projectPluginLinks, resolveUniquePluginIcons } from "./sidebar-plugins"

type ProjectIconPalette = {
  accent: string
  background: string
  foreground: string
}

export function ProjectPluginRail(props: {
  directory: Accessor<string>
  apps: Accessor<RuntimePluginApp[]>
  mobile?: boolean
  projectColor?: Accessor<string | undefined>
  projectPalette?: Accessor<ProjectIconPalette | undefined>
}) {
  const location = useLocation()
  const links = createMemo(() => projectPluginLinks({ directory: props.directory(), apps: props.apps() }))
  const icons = createMemo(() => resolveUniquePluginIcons(links()))
  const projectColorStyle = createMemo(() => {
    const palette = props.projectPalette?.()
    if (palette) {
      return {
        "--oa-project-rail-accent": palette.accent,
        "--oa-project-rail-wash": palette.background,
        "--oa-project-rail-foreground": palette.foreground,
      } as JSX.CSSProperties
    }
    const color = props.projectColor?.()
    if (!color) return
    const avatar = getAvatarColors(color)
    return {
      "--oa-project-rail-accent": avatar.foreground,
      "--oa-project-rail-wash": avatar.background,
    } as JSX.CSSProperties
  })
  const activePlugin = createMemo(() =>
    canonicalPluginAppID(new URLSearchParams(location.search).get("pluginApp") ?? ""),
  )
  const activeView = createMemo(() => new URLSearchParams(location.search).get("pluginView"))

  return (
    <Show when={links().length > 0}>
      <div
        data-sidebar-apps="rail"
        data-project-color={props.projectColor?.() || undefined}
        data-project-palette={props.projectPalette?.() ? "" : undefined}
        style={projectColorStyle()}
        class="shrink-0 w-full px-3 py-2 flex flex-col items-center gap-2"
      >
        <For each={links()}>
          {(item, index) => {
            const id = canonicalPluginAppID(item.id)
            const active = () =>
              activePlugin() === id && (id !== "marketing-calendar" || activeView() === "inspiration")
            const resolved = () =>
              id === "marketing-calendar" ? ({ kind: "icon", name: "sparkles" } as const) : icons()[index()]
            const accent = () => {
              const value = resolved()
              return value?.kind === "brand" ? value.brand.hex : pluginColorForIndex(index())
            }
            const icon = () => {
              const value = resolved()
              return value?.kind === "icon" ? value.name : item.icon
            }
            const brandPath = () => {
              const value = resolved()
              return value?.kind === "brand" ? value.brand.path : undefined
            }
            return (
              <Tooltip placement={props.mobile ? "bottom" : "right"} value={item.title}>
                <A
                  href={item.href}
                  data-sidebar-rail-item=""
                  data-sidebar-plugin-id={item.id}
                  aria-label={`Open ${item.title}`}
                  aria-current={active() ? "page" : undefined}
                  // Navigation alone only focuses the app when `?pluginApp=` actually changes — clicking
                  // the rail for an already-open app (or one opened, then switched away from in the panel)
                  // leaves the URL identical. Tell an open panel to focus the tab directly.
                  onClick={() => emitOpenPluginPanelTab({ directory: props.directory(), pluginID: id })}
                  style={{ "--sidebar-rail-delay": `${index() * 36}ms`, "--oa-rail-accent": accent() }}
                  class="flex size-10 items-center justify-center rounded-md text-text-base"
                  classList={{ "is-active text-text-strong": active() }}
                >
                  <PluginAppIcon
                    id={item.id}
                    icon={icon()}
                    title={item.title}
                    capabilities={item.capabilities}
                    size="normal"
                    brandPath={brandPath()}
                  />
                </A>
              </Tooltip>
            )
          }}
        </For>
      </div>
    </Show>
  )
}
